import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import VehicleChange from "../components/VehicleChange";
import DepotSelect, { useDepot } from "../components/DepotSelect";
import { routeChipStyle } from "../utils/routeColor";
import "../styles/analysis.css";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };

/**
 * 運行日を N 日ずらす。
 *
 * **new Date("2026-08-27") を使わないこと。** ISO 形式の日付だけを渡すと
 * UTC として解釈され、日本時間では前日になる。年月日に分解して渡す。
 */
function shiftServiceDate(date, days) {
  const [y, m, d] = date.split("-").map(Number);
  const shifted = new Date(y, m - 1, d + days);
  const pad = (n) => String(n).padStart(2, "0");

  return `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}-${pad(shifted.getDate())}`;
}

/**
 * その日の運行から、目を引くところだけを拾って並べる。
 *
 * 乗番一覧や車号ページは「調べに行く」画面だが、ここは**開いたら何かある**
 * 画面にしたい。だから車号も路線も入力させず、日付だけで開く。
 *
 * ## 空の出し方を分ける
 *
 * 「できごとが無かった」と「まだ出せない」はまったく違う。土日祝は乗番が
 * 組めていないので車両交代は原理的に出ない。そこを同じ「なし」で片付けると、
 * 静かな日だったと誤解される。has_duties で書き分ける。
 *
 * 代走も同じで、所定が 1 本も成立していなければ出ない。所定は濃い運行日が
 * 10 日たまらないと決まらないので、観測の浅い営業所ではしばらく空になる。
 * has_regulars で書き分ける。
 */
export default function AnalysisEventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // **営業所を決め打ちにしない。**足したときに URL を手で書き換えないと
  // 辿りつけなくなる。乗番がある営業所は API が知っている
  const [depot, setDepot] = useDepot(searchParams, setSearchParams);
  const date = searchParams.get("date");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!depot) return;      // 既定の営業所を引いている最中
    setLoading(true);
    setError(null);

    const query = new URLSearchParams({ depot, ...(date ? { date } : {}) });

    axios
      .get(`/analysis/events?${query}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.message ?? "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  }, [depot, date]);

  const goToDate = (next) => setSearchParams({ depot, date: next });

  const serviceDate = data?.service_date ?? date;
  const total =
    (data?.vehicle_changes.length ?? 0) +
    (data?.first_times.length ?? 0) +
    (data?.substitutions?.length ?? 0) +
    (data?.long_absences.length ?? 0);

  return (
    <div className="anl-page">
      <div className="anl-header">
        <button className="anl-back" onClick={() => navigate("/analysis")}>
          運用解析へ戻る
        </button>
        <h2 className="anl-title">
          本日のできごと
          <DepotSelect depot={depot} onChange={setDepot} />
        </h2>
        <p className="anl-subtitle">
          {data?.depot_name || (depot ? `${depot}営業所` : "")}の1日から、
          車両交代・初入線・代走・久しぶりの目撃を拾っています。
        </p>
      </div>

      {serviceDate && (
        <div className="anl-search">
          <button
            className="anl-daynav anl-daynav--prev"
            onClick={() => goToDate(shiftServiceDate(serviceDate, -1))}
            title="前日"
            aria-label="前日"
          />
          <input
            type="date"
            className="anl-input"
            value={serviceDate}
            onChange={(e) => goToDate(e.target.value)}
          />
          <button
            className="anl-daynav anl-daynav--next"
            onClick={() => goToDate(shiftServiceDate(serviceDate, 1))}
            /* 先の運行日は目撃が存在しないので進ませない */
            disabled={data?.is_today}
            title={data?.is_today ? "本日より先は表示できません" : "翌日"}
            aria-label="翌日"
          />
          {data && !data.is_today && (
            <button className="anl-btn" onClick={() => setSearchParams({ depot })}>
              本日に戻る
            </button>
          )}
        </div>
      )}

      {loading && <p className="anl-empty">読み込み中…</p>}
      {error && <div className="anl-warning">{error}</div>}

      {data && !loading && (
        <>
          <p className="anl-subtitle" style={{ margin: "0 0 .75rem" }}>
            <span className={`anl-badge anl-badge--${data.day_type}`}>
              {data.service_date}（{DAY_TYPE_LABEL[data.day_type] ?? data.day_type}）
            </span>{" "}
            {data.is_today ? "本日の運行日" : "過去の運行日"}
            {total > 0 && ` · ${total}件`}
          </p>

          {/* 当日は日が進むほど増える。空でも「無かった」と言い切らない */}
          {total === 0 && (
            <div className="anl-notice anl-notice--quiet">
              {data.is_today
                ? "いまのところ、目立ったできごとはありません。運行が進むと増えることがあります。"
                : "この日は目立ったできごとがありませんでした。"}
            </div>
          )}

          <div className="anl-card">
            <div className="anl-card__header">
              車両交代
              {data.vehicle_changes.length > 0 && (
                <span style={{ fontWeight: 400, color: "#6b7280" }}>
                  {data.vehicle_changes.length}乗番
                </span>
              )}
            </div>
            <div className="anl-card__body">
              {!data.has_duties ? (
                <p className="anl-empty">
                  {DAY_TYPE_LABEL[data.day_type] ?? data.day_type}の乗番はまだ組み立てられていません。
                  <br />
                  車両交代は乗番の並びを基準に見つけるため、この日は判定できません。
                </p>
              ) : data.vehicle_changes.length === 0 ? (
                <p className="anl-empty">この日は車両交代を検出していません。</p>
              ) : (
                data.vehicle_changes.map((c) => (
                  <VehicleChange
                    key={c.duty_id}
                    change={c}
                    dutyCode={c.duty_code}
                    dutyId={c.duty_id}
                    serviceDate={data.service_date}
                    depot={data.depot}
                    navigate={navigate}
                    lead={
                      <>
                        <button
                          className="anl-link"
                          style={{ fontWeight: 700 }}
                          onClick={() => navigate(`/analysis/duties/${c.duty_id}`)}
                        >
                          乗番{c.duty_code}
                        </button>
                        （{c.first_departure}〜{c.last_departure} · {c.trip_count}便）
                        {c.routes.map((r) => (
                          <span
                            key={r.route_id}
                            className="anl-route-badge"
                            style={{ ...routeChipStyle(r.color), marginLeft: ".25rem" }}
                          >
                            {r.route}
                          </span>
                        ))}
                      </>
                    }
                  />
                ))
              )}
              <p className="anl-note">
                1つの乗番を、日中に別の車両が引き継いだものです。目撃の無い便は区切りに使えないため、
                実際の交代時刻とは前後することがあります。
              </p>
            </div>
          </div>

          <div className="anl-card">
            <div className="anl-card__header">
              初入線
              {data.first_times.length > 0 && (
                <span style={{ fontWeight: 400, color: "#6b7280" }}>{data.first_times.length}件</span>
              )}
            </div>
            <div className="anl-card__body">
              {data.first_times.length === 0 ? (
                <p className="anl-empty">この日は初入線を検出していません。</p>
              ) : (
                data.first_times.map((f) => (
                  <div
                    key={`${f.bus_number}-${f.route_id}`}
                    className="anl-notice anl-notice--find"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/analysis/routes/${f.route_id}`)}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/analysis/routes/${f.route_id}`)}
                  >
                    <strong>初入線</strong> —{" "}
                    <button
                      className="anl-link"
                      style={{ fontWeight: 700 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/analysis/buses/${encodeURIComponent(f.bus_number)}?date=${data.service_date}&depot=${encodeURIComponent(data.depot)}`
                        );
                      }}
                    >
                      {f.bus_number}
                    </button>
                    が{f.route}（{f.route_full_name}）に入るのは記録上初めてです。
                    {f.at}から{f.trips}便。
                  </div>
                ))
              )}
              <p className="anl-note">
                目撃記録の中での「初めて」です。記録の始まりより前は分かりません。
                臨時・貸切・教習は数えていません。
              </p>
            </div>
          </div>

          {/* 代走。**所定が成立していないと原理的に出ない**ので、
              車両交代と同じく「起きなかった」と「まだ出せない」を書き分ける。 */}
          <div className="anl-card">
            <div className="anl-card__header">
              代走
              {data.substitutions?.length > 0 && (
                <span style={{ fontWeight: 400, color: "#6b7280" }}>
                  {data.substitutions.length}乗番
                </span>
              )}
            </div>
            <div className="anl-card__body">
              {!data.has_regulars ? (
                <p className="anl-empty">
                  この営業所・ダイヤ種別では所定運用を特定できていません。<br />
                  所定は直近10営業日のうち6日以上を同じ車両が占めた場合に認定します。
                  目撃情報がダイヤ種別ごとに10営業日分蓄積されていないか、日替わりで運用されている場合は表示されません。
                </p>
              ) : (data.substitutions?.length ?? 0) === 0 ? (
                <p className="anl-empty">この日は代走を検出していません。</p>
              ) : (
                data.substitutions.map((s) => (
                  <div
                    key={s.duty_id}
                    className="anl-notice anl-notice--find"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/analysis/duties/${s.duty_id}`)}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/analysis/duties/${s.duty_id}`)}
                  >
                    <strong>代走</strong> —{" "}
                    <button
                      className="anl-link"
                      style={{ fontWeight: 700 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/analysis/duties/${s.duty_id}`);
                      }}
                    >
                      {s.duty_code}
                    </button>
                    （{s.first_departure}〜{s.last_departure}・{s.trip_count}便）の所定は{" "}
                    <button
                      className="anl-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/analysis/buses/${encodeURIComponent(s.regular_bus)}?date=${data.service_date}&depot=${encodeURIComponent(data.depot)}`
                        );
                      }}
                    >
                      {s.regular_bus}
                    </button>
                    ですが、この日は{" "}
                    {s.buses.map((b, i) => (
                      <React.Fragment key={b.bus_number}>
                        {i > 0 && "・"}
                        <button
                          className="anl-link"
                          style={{ fontWeight: 700 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/analysis/buses/${encodeURIComponent(b.bus_number)}?date=${data.service_date}&depot=${encodeURIComponent(data.depot)}`
                            );
                          }}
                        >
                          {b.bus_number}
                        </button>
                        <span style={{ color: "#6b7280", fontSize: ".75rem" }}>
                          （{b.matched_trips}便）
                        </span>
                      </React.Fragment>
                    ))}
                    {" "}が入っています。
                  </div>
                ))
              )}
              <p className="anl-note">
                所定の車両がその日対象乗番に<strong>一度も運用に就かなかった場合</strong>を代走としています。
                所定の車両が走った上で途中から別の車両に代わった場合は、「代走」ではなく「車両交代」として扱います。
              </p>
            </div>
          </div>

          <div className="anl-card">
            <div className="anl-card__header">
              久しぶりの目撃
              {data.long_absences.length > 0 && (
                <span style={{ fontWeight: 400, color: "#6b7280" }}>
                  {data.long_absences.length}台
                </span>
              )}
            </div>
            <div className="anl-card__body">
              {data.long_absences.length === 0 ? (
                <p className="anl-empty">この日は長く空いていた車両の目撃はありません。</p>
              ) : (
                data.long_absences.map((a) => (
                  <div key={a.bus_number} className="anl-notice anl-notice--find">
                    <strong>{a.days}日ぶり</strong> —{" "}
                    <button
                      className="anl-link"
                      style={{ fontWeight: 700 }}
                      onClick={() =>
                        navigate(
                          `/analysis/buses/${encodeURIComponent(a.bus_number)}?date=${data.service_date}&depot=${encodeURIComponent(data.depot)}`
                        )
                      }
                    >
                      {a.bus_number}
                    </button>
                    を{a.at}に確認しました（前回{a.previous_seen_on}）。
                  </div>
                ))
              )}
              <p className="anl-note">
                <strong>「見えていなかった」だけの可能性があります。</strong>
                目撃情報が薄い時期は、運行していても記録に残りません。入場・休車と断定はできません。
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
